import {
  Checkbox,
  FormControlLabel,
  Grid,
  Typography
} from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';
import React from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  MessageDescriptor
} from 'react-intl';
import { useDispatch } from 'react-redux';
import PaymentTypes from '../CurrencyPay/PaymentTypes';
import { PaymentTermStatus } from '../redux/models/paymentTerms';
import { Dispatch } from '../redux/store';
import useStyles from '../useStyles';

type InstallmentProps = {
  term: TermProps;
  anyPaid: boolean;
};

export type TermProps = {
  termOrder: number;
  dueUpon: string;
  dueDate?: Date;
  sellPrice: number;
  percentPrice: number;
  checked: boolean;
  currencyPayStatus: string;
  paymentStatus?: PaymentTermStatus;
  paymentType?: PaymentTypes;
  changedAtUTC: Date;
};

const getControl = function getControl(
  term: TermProps,
  className: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: any
): React.ReactElement {
  if (term.paymentStatus === PaymentTermStatus.Paid) {
    return (
      <div className={className}>
        <Check />
      </div>
    );
  }

  if (term.paymentStatus === PaymentTermStatus.PaymentUnsuccessful) {
    return (
      <div className={className}>
        <Close />
      </div>
    );
  }

  const disabled =
    term.paymentStatus === PaymentTermStatus.Due ||
    term.paymentStatus === PaymentTermStatus.Pending ||
    term.paymentStatus === PaymentTermStatus.UnderReview ||
    term.paymentStatus === PaymentTermStatus.Unknown;
  return (
    <Checkbox
      color='primary'
      disabled={disabled}
      checked={term.checked}
      onChange={() => dispatch.paymentTerms.toggleSelectedTerm(term.termOrder)}
    />
  );
};

const getPaymentStatusMessage = function getPaymentStatusMessage(
  status?: PaymentTermStatus,
  currencyPayStatus?: string
): MessageDescriptor {
  switch (status) {
    case PaymentTermStatus.Due:
      return { id: 'PaymentTerms.Due', defaultMessage: 'Due' };
    case PaymentTermStatus.Paid:
      return { id: 'PaymentTerms.Paid', defaultMessage: 'Paid' };
    case PaymentTermStatus.PaymentUnsuccessful:
      return {
        id: 'PaymentTerms.PaymentUnsuccessful',
        defaultMessage: 'Payment Unsuccessful'
      };
    case PaymentTermStatus.Pending:
      return { id: 'PaymentTerms.Pending', defaultMessage: 'Pending' };
    case PaymentTermStatus.Unpaid:
      return { id: 'PaymentTerms.Unpaid', defaultMessage: 'Unpaid' };
    case PaymentTermStatus.UnderReview:
      return { id: 'PaymentTerms.UnderReview', defaultMessage: 'Under Review' };
    case PaymentTermStatus.Unknown:
      return { id: 'PaymentTerms.Unknown', defaultMessage: currencyPayStatus };
    default:
      return { id: 'PaymentTerms.Unknown', defaultMessage: 'Unknown' };
  }
};

const getPaymentTypeMessage = function getPaymentTypeMessage(
  paymentType?: PaymentTypes
): MessageDescriptor {
  switch (paymentType) {
    case PaymentTypes.AmericanExpress:
    case PaymentTypes.DinersClub:
    case PaymentTypes.Discover:
    case PaymentTypes.MasterCard:
    case PaymentTypes.Visa:
      return { id: 'PaymentTerms.CreditCard', defaultMessage: 'Credit Card' };
    case PaymentTypes.Debit:
      return { id: 'PaymentTerms.DebitCard', defaultMessage: 'Debit Card' };
    case PaymentTypes.Checking:
    case PaymentTypes.CorporateChecking:
    case PaymentTypes.CorporateSavings:
    case PaymentTypes.Savings:
      return {
        id: 'PaymentTerms.BankTransfer',
        defaultMessage: 'Bank Transfer'
      };
    case PaymentTypes.Financing:
      return { id: 'PaymentTerms.UnderReview', defaultMessage: 'Financing' };
    default:
      return {
        id: 'PaymentTerms.Unknown',
        defaultMessage: 'Unknown'
      };
  }
};

const Installment = function Installment({
  term,
  anyPaid
}: InstallmentProps): React.ReactElement {
  const dispatch = useDispatch<Dispatch>();
  const classes = useStyles();
  const control = getControl(term, classes.installmentsSpacing, dispatch);
  const paymentStatusMessage = getPaymentStatusMessage(
    term.paymentStatus,
    term.currencyPayStatus
  );
  const paymentTypeMessage = getPaymentTypeMessage(term.paymentType);
  const shouldDisplayChangedAtDate =
    term.paymentStatus !== PaymentTermStatus.Due &&
    term.paymentStatus !== PaymentTermStatus.Unpaid;

  const shouldDisplayDueDate = term.dueDate != null;
  return (
    <Grid container justify='flex-end' direction='row' alignItems='center'>
      <Grid item xs={anyPaid ? 1 : 1}>
        <FormControlLabel label={term.termOrder + 1} control={control} />
      </Grid>
      <Grid item xs={anyPaid ? 1 : 3}>
        <Typography>{term.dueUpon}</Typography>
      </Grid>
      <Grid item xs={anyPaid ? 1 : 2}>
        {shouldDisplayDueDate && (
          <Typography>
            <FormattedDate value={term.dueDate} format='MM/DD/YYYY' />
          </Typography>
        )}
      </Grid>
      <Grid item xs={anyPaid ? 1 : 3}>
        <Typography>
          <FormattedNumber
            style='percent'
            value={term.percentPrice}
            maximumSignificantDigits={4}
          />
        </Typography>
      </Grid>
      <Grid item xs={anyPaid ? 1 : 3}>
        <Typography>
          <FormattedNumber
            style='currency'
            currency='USD'
            value={term.sellPrice}
          />
        </Typography>
      </Grid>
      {anyPaid && (
        <>
          <Grid item xs={2}>
            <Typography>
              <FormattedMessage
                id={paymentStatusMessage.id}
                defaultMessage={paymentStatusMessage.defaultMessage}
              />
            </Typography>
          </Grid>
          <Grid item xs={3}>
            {shouldDisplayChangedAtDate && (
              <Typography>
                <FormattedDate
                  value={term.changedAtUTC}
                  month='numeric'
                  day='numeric'
                  year='numeric'
                  hour12
                  hour='numeric'
                  minute='numeric'
                  second='numeric'
                  timeZoneName='short'
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={2}>
            <Typography>
              {Number.isInteger(term.paymentType) && (
                <FormattedMessage
                  id={paymentTypeMessage.id}
                  defaultMessage={paymentTypeMessage.defaultMessage}
                />
              )}
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default Installment;
